import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import { debounce, isEqual } from 'es-toolkit'
import { useRouter } from 'next/router'
import { useQueryState } from 'nuqs'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import { useCreateOpportunityChatSession } from '@/components/chat-v2/hooks/useChatSession'

import {
  DEFAULT_FEDERAL_CONTRACT_FILTER,
  removeAwardNoticeFromFederalContractScheduleFilters,
} from '@/constants/search/federal/contract'
import { DEFAULT_DIBBS_FILTER } from '@/constants/search/federal/dibbs'
import { DEFAULT_FEDERAL_GRANT_FILTER } from '@/constants/search/federal/grant'
import { DEFAULT_SBIR_FILTER } from '@/constants/search/federal/sbir'
import { DEFAULT_SLED_CONTRACT_FILTER } from '@/constants/search/sled/contract'

import { parseQueryString } from '@/helpers/search/url'
import { removeCodeMarkdown } from '@/helpers/string'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useCreateSchedule } from '@/hooks/schedules/useCreateSchedule'
import { useSearchExport } from '@/hooks/search/useExportSearch'

// import type { CreateThread, ThreadType } from '@/types/chat/Thread'
import type { HeaderConfig } from '@/types/components/SearchFilter'
import type { OpportunityType } from '@/types/Opportunity'
import type { CreateSchedule } from '@/types/Schedule'
import type {
  SearchOut,
  SearchRequest,
  SearchRequestFilter,
  SearchStore,
} from '@/types/Search'

export interface SearchContextProps extends SearchStore {
  exportIsPending: boolean
  filterHeaderConfig: HeaderConfig
  type: OpportunityType
  validSummaryIsLoading: boolean
  createNewRequest: (query: string, filter?: SearchRequestFilter) => void
  handleCreateSchedule: () => void
  handleCreateThread: (item: SearchOut) => Promise<void>
  handleExport: () => Promise<void>
  handleInfiniteScroll: (offset: number) => void
  handleRequest: (request: SearchRequest) => void
  submitRequest: (request: SearchRequest) => void
  toggleDrawer: () => void
}

export interface SearchContextProviderProps {
  type: OpportunityType
  store: SearchStore
  children?: React.ReactNode
}

export const SearchContext = createContext<SearchContextProps>({
  augmentedQueries: [],
  autoSubmit: true,
  createdPursuits: new Map(),
  data: {
    count: 0,
    data: [],
    has_more: false,
  },
  dataIsLoading: false,
  exportIsPending: false,
  filter: null,
  filterDrawerIsOpen: false,
  filterHeaderConfig: { show: true },
  isStreaming: false,
  paginationIsLoading: false,
  query: '',
  request: undefined,
  previousRequest: undefined,
  steps: [],
  summary: '',
  summaryIsLoading: false,
  type: 'federal_contract',
  validSummaryIsLoading: false,
  useFullTextSearch: false,
  addCreatedPursuit: () => {},
  createNewRequest: () => {},
  handleCreateSchedule: () => {},
  handleCreateThread: () => Promise.resolve(),
  handleExport: () => Promise.resolve(),
  handleInfiniteScroll: () => {},
  handleRequest: () => {},
  removeCreatedPursuit: () => {},
  setAugmentedQueries: () => {},
  setAutoSubmit: () => {},
  setData: () => {},
  setDataIsLoading: () => {},
  setFilter: () => {},
  setFilterDrawerIsOpen: () => {},
  setIsStreaming: () => {},
  setQuery: () => {},
  setRequest: () => {},
  setPreviousRequest: () => {},
  setSteps: () => {},
  setSummary: () => {},
  setSummaryIsLoading: () => {},
  submitRequest: () => {},
  setUseFullTextSearch: () => {},
  toggleDrawer: () => {},
})

const filterHeaderConfig: HeaderConfig = { show: true }

export const SearchContextProvider: React.FC<SearchContextProviderProps> = ({
  type,
  store,
  children,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { accessToken } = useAuthInfo()
  const { mutateAsync: createOpportunityChatSession } =
    useCreateOpportunityChatSession()
  const { mutateAsync: createSchedule } = useCreateSchedule({
    orgId: activeOrgId,
  })
  const { exportSearch, isPending } = useSearchExport()
  const { handleError } = useHandleError()

  const {
    autoSubmit,
    data,
    filter,
    paginationIsLoading,
    query,
    request,
    previousRequest,
    summaryIsLoading,
    useFullTextSearch,
    setFilter,
    setFilterDrawerIsOpen,
    setQuery,
    setRequest,
  } = store

  const router = useRouter()

  const [, setOrgId] = useQueryState('organization_id')

  const validSummaryIsLoading = useMemo(() => {
    if (request === undefined) {
      return summaryIsLoading
    }
    return request.query === '' ? false : summaryIsLoading
  }, [request, summaryIsLoading])

  const getFilter = useCallback(() => {
    if (filter) {
      return filter
    }
    switch (type) {
      case 'dibbs_contract':
        return {
          type: 'dibbs_contract',
          filter: DEFAULT_DIBBS_FILTER,
        } as SearchRequestFilter
      case 'federal_contract':
        return {
          type: 'federal_contract',
          filter: filter ?? DEFAULT_FEDERAL_CONTRACT_FILTER,
        } as SearchRequestFilter
      case 'federal_grant':
        return {
          type: 'federal_grant',
          filter: DEFAULT_FEDERAL_GRANT_FILTER,
        } as SearchRequestFilter
      case 'sbir':
        return {
          type: 'sbir',
          filter: DEFAULT_SBIR_FILTER,
        } as SearchRequestFilter
      case 'sled_contract':
        return {
          type: 'sled_contract',
          filter: DEFAULT_SLED_CONTRACT_FILTER,
        } as SearchRequestFilter
      default:
        const _exhaustiveCheck: never = type
        throw new Error(`Unhandled filter type: ${_exhaustiveCheck}`)
    }
  }, [filter, type])

  const handleCreateThread = useCallback(
    async (item: SearchOut) => {
      if (!activeOrgId) {
        return
      }
      try {
        const session = await createOpportunityChatSession({
          opportunity_id: item.id,
          opportunity_type: type,
          title: removeCodeMarkdown(item.title),
          organization_id: activeOrgId,
        })
        if (session) {
          window.open(`/orgs/${activeOrgId}/c/opp/${session.id}`, '_blank')
        }
      } catch (err) {
        console.error(err)
      }
    },
    [activeOrgId, type, createOpportunityChatSession],
  )

  const handleCreateSchedule = useCallback(async () => {
    if (!request || !activeOrgId) {
      return
    }
    try {
      let req: CreateSchedule | null = null
      const type = request.filter.type
      switch (type) {
        case 'dibbs_contract':
          req = {
            query,
            interval: 'daily',
            ignored_user_ids: [],
            dibbs_contract_schedule: {
              filters: request.filter.filter ?? DEFAULT_DIBBS_FILTER,
            },
            type: 'dibbs_contract_schedule',
            organization_id: activeOrgId,
          }
          break
        case 'federal_contract':
          req = {
            query,
            interval: 'daily',
            ignored_user_ids: [],
            federal_contract_schedule: {
              // award notices should not be included in schedules
              filters: removeAwardNoticeFromFederalContractScheduleFilters(
                request.filter.filter ?? DEFAULT_FEDERAL_CONTRACT_FILTER,
              ),
            },
            type: 'federal_contract_schedule',
            organization_id: activeOrgId,
          }
          break
        case 'federal_grant':
          req = {
            query,
            interval: 'daily',
            ignored_user_ids: [],
            federal_grant_schedule: {
              filters: request.filter.filter ?? DEFAULT_FEDERAL_GRANT_FILTER,
            },
            type: 'federal_grant_schedule',
            organization_id: activeOrgId,
          }
          break
        case 'sbir':
          req = {
            query,
            interval: 'daily',
            ignored_user_ids: [],
            sbir_schedule: {
              filters: request.filter.filter ?? DEFAULT_SBIR_FILTER,
            },
            type: 'sbir_schedule',
            organization_id: activeOrgId,
          }
          break
        case 'sled_contract':
          req = {
            query,
            interval: 'daily',
            ignored_user_ids: [],
            sled_contract_schedule: {
              filters: request.filter.filter ?? DEFAULT_SLED_CONTRACT_FILTER,
            },
            type: 'sled_contract_schedule',
            organization_id: activeOrgId,
          }
          break
        default:
          const _exhaustiveCheck: never = type
          throw new Error(`Unhandled schedule type: ${_exhaustiveCheck}`)
      }
      if (req) {
        await createSchedule(req)
        toast.success('Successfully created saved search!', {
          action: {
            label: 'View',
            onClick: () => {
              window.open(`/orgs/${activeOrgId}/schedules`, '_blank')
            },
          },
        })
      }
    } catch (err) {}
  }, [activeOrgId, request, createSchedule])

  const handleExport = useCallback(async () => {
    if (request) {
      await exportSearch(request)
    }
  }, [request, exportSearch])

  const handleInfiniteScroll = useCallback(
    (offset: number) => {
      if (request && !paginationIsLoading) {
        setRequest(
          {
            ...request,
            offset: offset,
          },
          accessToken,
        )
      }
    },
    [accessToken, request, paginationIsLoading, setRequest],
  )

  const handleRequest = useCallback(
    (request: SearchRequest) => {
      setRequest(request, accessToken)
    },
    [accessToken, setRequest],
  )

  const toggleDrawer = useCallback(() => {
    setFilterDrawerIsOpen(false)
  }, [setFilterDrawerIsOpen])

  const submitRequest = useCallback(
    debounce((request: SearchRequest) => {
      setQuery(request.query)
      setFilter(request.filter)
      setRequest(request, accessToken)
    }, 100),
    [accessToken, setFilter, setQuery, setRequest],
  )

  const createNewRequest = useCallback(
    (query: string, filter?: SearchRequestFilter) => {
      const newFilter = filter ?? getFilter()
      const request = {
        query,
        type: type,
        organization_id: activeOrgId,
        filter: newFilter,
        use_fts: useFullTextSearch,
      }
      submitRequest(request)
    },
    [
      activeOrgId,
      filter,
      query,
      submitRequest,
      type,
      useFullTextSearch,
      getFilter,
    ],
  )

  // Use router.asPath for stable URL change detection
  // router.query is an object that may change reference on every render
  const urlPath = router.asPath

  useEffect(() => {
    if (activeOrgId !== '') {
      if (router.query !== undefined) {
        const initialSearchRequest = parseQueryString(router.query, type)
        initialSearchRequest.organization_id = activeOrgId
        submitRequest.cancel()
        submitRequest(initialSearchRequest)
        setOrgId(activeOrgId)
      }
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [activeOrgId, urlPath])

  useEffect(() => {
    if (autoSubmit) {
      // do NOT use submitRequest here, it will cause an infinite loop,
      // since we're setting setFilter within it
      const filter = getFilter()
      const request = {
        query,
        type,
        filter,
        organization_id: activeOrgId,
        use_fts: useFullTextSearch,
      }
      if (!isEqual(request, previousRequest)) {
        setQuery(query)
        setRequest(request, accessToken)
      }
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [filter])

  return (
    <SearchContext.Provider
      value={{
        ...store,
        exportIsPending: isPending,
        filterHeaderConfig,
        type,
        validSummaryIsLoading,
        createNewRequest,
        handleCreateSchedule,
        handleCreateThread,
        handleExport,
        handleInfiniteScroll,
        handleRequest,
        submitRequest,
        toggleDrawer,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export const useSearchContext = () => {
  return useContext(SearchContext)
}
